import React, { useCallback, useEffect, useMemo, useState } from 'react';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { Loading } from '@this/shared/ui/feedbacks/loading/loading';
import { Button } from '@this/shared/ui/inputs/button';
import { Link } from '@this/shared/ui/navigations/link';
import { useAITTheme } from '@this/shared/ui/theme';
import { Fetcher } from '@this/src/util';
import type { KnowledgeImageArg } from '@this/domain/knowledge_image/knowledge_image';
import KnowledgeImage, { Status } from '@this/domain/knowledge_image/knowledge_image';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@this/components/shared/ui/data_displays/table';
import KnowledgeImageModal from '@this/components/admin/KnowledgeImages/knowledge_image_modal';
import { getStyles } from './knowledge_images.styles';

interface Response {
  knowledge_images: KnowledgeImageArg[];
}

const KnowledgeImages: React.FC = () => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [knowledgeImages, setKnowledgeImages] = useState<KnowledgeImage[]>([]);
  const [knowledgeImage, setKnowledgeImage] = useState<KnowledgeImage | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalType, setModalType] = useState<string>('');

  const fetchKnowledgeImages = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await Fetcher.get<Response>('/admin/knowledge_images.json');
      setKnowledgeImages(response.knowledge_images.map(ki => new KnowledgeImage(ki)));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchKnowledgeImages();
  }, [fetchKnowledgeImages]);

  const openModal = useCallback((type: string, selected: KnowledgeImage | null) => {
    setKnowledgeImage(selected);
    setModalType(type);
    setShowModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
  }, []);

  return (
    <div style={styles.pageLayout}>
      <div style={styles.pageHeader}>
        <h2 style={styles.pageTitle}>ファイルアップロード</h2>
        <Button
          type="button"
          color="primary"
          style={styles.addButton}
          disabled={isLoading}
          onClick={() => openModal('add', null)}
        >
          ファイルを追加
        </Button>
      </div>
      <div style={styles.contentIn}>
        {isLoading ? (
          <Loading />
        ) : (
          <div style={styles.tableWrapper}>
            {knowledgeImages.length === 0 ? (
              <div style={styles.emptyMessage}>ファイルが登録されていません</div>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell style={styles.tableHeaderCell}>名前</TableCell>
                    <TableCell style={styles.tableHeaderCell}>共有URL</TableCell>
                    <TableCell style={styles.tableHeaderCell}>公開ステータス</TableCell>
                    <TableCell style={styles.tableHeaderCellActions}>操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {knowledgeImages.map(data => {
                    const statusBadgeStyle = {
                      ...styles.statusBadge,
                      ...(data.status === 'login_only' ? styles.statusBadgeLoginOnly : {})
                    };

                    return (
                      <TableRow hover key={data.id} data-e2e-id={data.id}>
                        <TableCell className="e2e-name" style={styles.tableBodyCell}>
                          {data.name}
                        </TableCell>
                        <TableCell className="e2e-url" style={styles.tableBodyCellUrl}>
                          {data.shareUrl}
                        </TableCell>
                        <TableCell className="e2e-status" style={styles.tableBodyCell}>
                          <span style={statusBadgeStyle}>{Status[data.status]}</span>
                        </TableCell>
                        <TableCell style={styles.tableBodyCellActions}>
                          <div style={styles.rowActions}>
                            <Link target="_blank" href={data.s3Url} isExternal style={styles.viewLink}>
                              表示
                              <OpenInNewIcon style={styles.viewLinkIcon} />
                            </Link>
                            <Button
                              type="button"
                              color="primary"
                              size="small"
                              style={styles.tableActionButton}
                              disabled={isLoading}
                              onClick={() => openModal('mod', data)}
                            >
                              上書き
                            </Button>
                            <Button
                              type="button"
                              color="danger"
                              size="small"
                              style={styles.tableActionButton}
                              disabled={isLoading}
                              onClick={() => openModal('delete', data)}
                            >
                              削除
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        )}
      </div>
      {showModal && (
        <KnowledgeImageModal
          open
          onClose={closeModal}
          afterSubmit={fetchKnowledgeImages}
          id={knowledgeImage?.id}
          fileName={knowledgeImage?.name}
          fileStatus={knowledgeImage?.status}
          modalType={modalType}
        />
      )}
    </div>
  );
};

export default KnowledgeImages;
